import React, { useEffect, useMemo, useState } from 'react';
import { Text } from '../../../../components/typography';
import { useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import styles from './ReportBuildMenuStats.module.scss';
import TextWithIcon from '../../../../components/typography/TextWithIcon';
import { ResponsiveBar } from '@nivo/bar';
import { Pie as NivoPie } from '@nivo/pie';
import { bootstrapColors } from '../../utilities';
import Icon from '../../../../components/Icon';
import {
  customColors,
  inputTypeToIconMap,
  multiLineTruncate,
} from '../../../../utilities';
import { getLocalTime } from '../../../../utilities/time';
import { useCurrentUser } from '../../../../providers/UserProvider';
import { getAllQuestionsFlatMap, hasFollowUpQuestion } from './stats';
import findFileIcon, { imageExtensions } from '../../../../utilities/files';
import { downloadFile } from '../../../../utilities/inspection';
import {
  calculateRisk,
  colorLookup,
  defaultRanges,
} from '../../../../utilities/riskMatrix';
import { openFileViewer } from '../../../../graphql/cache/modal';
import SimpleWorkspaceCard from '../../../../components/SimpleWorkspaceCard';
import { ResponsiveLine } from '@nivo/line';
import { Form, Spinner } from 'react-bootstrap';
import { graphTypes } from '../../../../utilities/reports';
import Spacer from '../../../../components/typography/Spacer';

const splitPipe = (s) =>
  String(s ?? '')
    .split('|')
    .map((t) => t.trim())
    .filter(Boolean);

function fillMissingRows(stats, q, inputType) {
  if (!stats?.rows) return stats;
  const type = String(inputType || q?.inputType || q?.type || '').toUpperCase();

  let templateOpts = splitPipe(q?.additionalData || q?.additional);
  if (!templateOpts.length && type === 'TEST') {
    templateOpts = ['PASS', 'FAIL', 'NOT_APPLICABLE'];
  }
  if (!templateOpts.length) return stats;

  const existingLabels = new Set(
    stats.rows.map((r) => String(r.label ?? r.key ?? '')),
  );
  const missing = templateOpts.filter((opt) => !existingLabels.has(opt));
  if (!missing.length) return stats;

  return {
    ...stats,
    rows: [
      ...stats.rows,
      ...missing.map((opt) => ({
        key: opt,
        label: opt,
        count: 0,
        value: 0,
        pct: 0,
      })),
    ],
  };
}

const chartCompatibleTypes = new Set([
  'SELECT_SINGLE',
  'DROPDOWN_SINGLE',
  'USER',
  'TEST',
  'ASSET',
  'SELECT_MULTI',
  'DROPDOWN_MULTI',
  'SELECT_CHECKLIST',
  'USER_MULTI',
  'SIGNATURE_SINGLE',
  'SIGNATURE_MULTI',
  'MATRIX',
]);

const chartGraphTypes = graphTypes.filter((t) => t.key !== 'PIVOT');

const itemQuery = loader('./ReportBuildMenuStats.items.graphql');

function ReportBuildMenuStats({
  options,
  setMenuState,
  isEditing,
  setStatsPDF = () => {},
  attachmentId,
}) {
  const { company } = useCurrentUser();
  const templateId = Number(options?.template?.id);
  const { daysBack, timeRange } = options || {};
  const { to, from } = timeRange || {};
  const { user } = useCurrentUser();
  const isMilitary = user?.timeSetting === 'military';
  const { questions = [] } = options || {};
  const days = useMemo(() => {
    const num = parseInt(daysBack);
    if (!isNaN(num)) return daysBack;
    const start = getLocalTime(from || timeRange?.from);
    const end = getLocalTime(to || timeRange?.to);
    return Math.ceil(end.diff(start, 'days', true));
  }, [daysBack, from, timeRange?.from, timeRange?.to, to]);

  const workspacesVar = useMemo(() => {
    const ws = options?.workspaces;
    return Array.isArray(ws) && ws.length
      ? ws.map((w) => String(w.id))
      : undefined;
  }, [options?.workspaces]);

  const vars = useMemo(() => {
    if (!options?.questions?.length || !from || !to) return undefined;

    const questionIds = options?.questions?.map(({ id }) => id);
    return {
      questionIds,
      workspaces: workspacesVar,
      options: {
        filters: [
          { field: 'dateCreated', operator: 'between', value: [from, to] },
        ],
      },
    };
  }, [options?.questions, workspacesVar, from, to]);

  const {
    loading,
    error,
    data: { inspectionStatsByQuestion = [] } = {},
  } = useQuery(itemQuery, {
    skip: !vars,
    variables: vars,
    fetchPolicy: 'network-only',

    notifyOnNetworkStatusChange: false,
    returnPartialData: false,
  });

  const statsByQuestionId = useMemo(
    () =>
      new Map(
        inspectionStatsByQuestion.map(({ questionId, inputType, stats }) => [
          String(questionId),
          { stats, inputType },
        ]),
      ),
    [inspectionStatsByQuestion],
  );
  const inspectionCount = useMemo(() => {
    if (!inspectionStatsByQuestion.length) return 0;
    return Math.max(
      ...inspectionStatsByQuestion.map((e) => e.stats?.total ?? 0),
    );
  }, [inspectionStatsByQuestion]);

  useEffect(() => {
    if (!Number.isFinite(templateId)) return;
    setMenuState((prev) => {
      if (prev?.total === inspectionCount) return prev;
      return { ...prev, total: inspectionCount };
    });
  }, [inspectionCount, templateId, setMenuState]);

  useEffect(() => {
    if (!Number.isFinite(templateId)) return;
    setMenuState((prev) => ({
      ...prev,
      title: prev?.title ?? options?.template?.title,
      type: 'STATS',
    }));
  }, [templateId, setMenuState, options?.template?.title]);

  const getQId = React.useCallback((x) => String(x?.id ?? x?.questionId), []);

  useEffect(() => {
    if (!attachmentId) {
      setStatsPDF({});
      return;
    }
    const selected = Array.isArray(options?.questions) ? options.questions : [];
    const sections = selected.map((q) => {
      const qKey = getQId(q);
      const entry = statsByQuestionId.get(qKey);
      const rawStats = entry?.stats ?? null;
      const inputType = entry?.inputType ?? q.inputType;
      const graphType = q.graphType || options?.graphType || 'BAR_HORIZONTAL';
      const stats = fillMissingRows(rawStats, q, inputType);
      return { id: qKey, title: q.title, inputType, stats, graphType };
    });

    setStatsPDF((prev) => {
      const prevData = prev[attachmentId];
      if (JSON.stringify(prevData) === JSON.stringify(sections)) return prev;
      return { ...prev, [attachmentId]: sections };
    });
  }, [
    attachmentId,
    options?.questions,
    options?.graphType,
    statsByQuestionId,
    getQId,
    setStatsPDF,
  ]);

  const allSelected = useMemo(() => {
    if (!templateId) return false;
    const allQuestions =
      options.template?.categories?.flatMap((c) =>
        getAllQuestionsFlatMap(c.questions),
      ) || [];
    return allQuestions.length === options.questions?.length;
  }, [options.questions?.length, options.template?.categories, templateId]);

  const onSelectAll = () => {
    const allQs =
      options?.template?.categories?.flatMap((c) =>
        getAllQuestionsFlatMap(c.questions),
      ) ?? [];
    setMenuState((prev) => ({ ...prev, questions: allSelected ? [] : allQs }));
  };

  return (
    <div>
      <div className={styles.reportHeader}>
        <div>
          <Text weight="semiBold" size="md-lg" noMargin>
            {options?.title || 'No Template Selected'}
          </Text>
          <Text size="sm" noMargin>
            {options?.description ||
              `${getLocalTime(from).format(
                isMilitary ? 'MMMM Do, YYYY HH:mm' : 'MMMM Do, YYYY h:mma',
              )} - ${getLocalTime(to).format(
                isMilitary ? 'MMMM Do, YYYY HH:mm' : 'MMMM Do, YYYY h:mma',
              )}`}
          </Text>
        </div>
        <div>
          <Text textAlign="right" noMargin weight="semiBold">
            {`Total: ${inspectionCount}`}
          </Text>
          <TextWithIcon
            leftIcon="timer"
            color="secondary"
            noMargin
            weight="semiBold"
            size="sm"
            iconProps={{ fontSize: '1.2rem' }}
          >
            {`${days} ${days > 1 ? 'days' : 'day'}`}
          </TextWithIcon>
        </div>
      </div>
      <div className={styles.workspaces}>
        {options?.workspaces?.length ? (
          options.workspaces?.map((w) => (
            <div key={w.id}>
              <SimpleWorkspaceCard workspace={w} size="sm" weight="regular" />
            </div>
          ))
        ) : (
          <TextWithIcon
            iconProps={{ fontSize: '1rem', color: customColors.GREEN }}
            leftIcon="check_circle"
            size="sm"
          >
            All Workspaces
          </TextWithIcon>
        )}
      </div>
      <div className={styles.contentContainer}>
        {error && (
          <TextWithIcon size="sm" color="orange" icon={'warning_amber'}>
            Unable to retrieve data, adjust your options
          </TextWithIcon>
        )}
        {isEditing ? (
          <div>
            <Spacer />
            {!!templateId && (
              <Text
                size="sm"
                weight="semiBold"
                color="accentPrimary"
                hover
                noMargin
                onClick={onSelectAll}
              >
                {allSelected ? 'De-Select All' : `Select All`}
              </Text>
            )}
            {!templateId ? (
              <div>
                <br />
                <Text textAlign="center" weight="semiBold" color="yellow">
                  {'Select a Template'}
                </Text>
                <br />
              </div>
            ) : (
              options.template?.categories?.map((cat) => (
                <div key={cat.id} className={styles.questions}>
                  <div className={styles.header}>
                    <Text weight="semiBold" noMargin>
                      {cat.title}
                    </Text>
                  </div>
                  <div>
                    {cat.questions?.map((q) => {
                      const isShown = questions.some((sq) => sq.id === q.id);

                      return q?.inputType !== 'MESSAGE' ? (
                        <div className={styles.section} key={q.id}>
                          <div className={styles.flexRow}>
                            {isEditing && (
                              <Form.Check
                                checked={isShown}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setMenuState((prev) => {
                                    const prevQs = prev?.questions || [];
                                    const nextQs = checked
                                      ? // add if not already present
                                        prevQs.some((sq) => sq.id === q.id)
                                        ? prevQs
                                        : [...prevQs, q]
                                      : // remove by id
                                        prevQs.filter((sq) => sq.id !== q.id);

                                    return { ...prev, questions: nextQs };
                                  });
                                }}
                              />
                            )}
                            <Icon>{inputTypeToIconMap[q.inputType]}</Icon>
                            <div className={styles.header}>
                              <Text weight="semiBold" size="sm-md" noMargin>
                                {q.title}
                              </Text>
                              {isShown &&
                                chartCompatibleTypes.has(q.inputType) && (
                                  <div className={styles.chartIcons}>
                                    {chartGraphTypes.map((t) => {
                                      const qInList = questions.find(
                                        (sq) => sq.id === q.id,
                                      );
                                      const isSelected =
                                        (qInList?.graphType ||
                                          options?.graphType ||
                                          'BAR_HORIZONTAL') === t.key;
                                      return (
                                        <Icon
                                          key={t.key}
                                          hover
                                          noMargin
                                          style={{
                                            fontSize: '1.1rem',
                                            color: isSelected
                                              ? customColors.BLUE
                                              : customColors.SECONDARY_LIGHT,
                                            ...t.iconProps,
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setMenuState((prev) => ({
                                              ...prev,
                                              questions: prev.questions.map(
                                                (sq) =>
                                                  sq.id === q.id
                                                    ? {
                                                        ...sq,
                                                        graphType: t.key,
                                                      }
                                                    : sq,
                                              ),
                                            }));
                                          }}
                                        >
                                          {t.icon}
                                        </Icon>
                                      );
                                    })}
                                  </div>
                                )}
                            </div>
                          </div>
                          {hasFollowUpQuestion(q) &&
                            (Array.isArray(q.conditionalAction)
                              ? q.conditionalAction.flatMap(
                                  (ca) => ca.actions ?? [],
                                )
                              : []
                            )
                              .filter(
                                (a) =>
                                  a?.action === 'FOLLOW_UP_QUESTION' &&
                                  a.actionable?.inputType !== 'MESSAGE',
                              )
                              .map(({ actionable: fq }) => {
                                const isShown = questions.some(
                                  (sq) => sq.id === fq.id,
                                );
                                return (
                                  <div className={styles.flexRow}>
                                    {isEditing && (
                                      <Form.Check
                                        checked={isShown}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setMenuState((prev) => {
                                            const prevQs =
                                              prev?.questions || [];
                                            const nextQs = checked
                                              ? // add if not already present
                                                prevQs.some(
                                                  (sq) => sq.id === fq.id,
                                                )
                                                ? prevQs
                                                : [...prevQs, fq]
                                              : // remove by id
                                                prevQs.filter(
                                                  (sq) => sq.id !== fq.id,
                                                );

                                            return {
                                              ...prev,
                                              questions: nextQs,
                                            };
                                          });
                                        }}
                                      />
                                    )}
                                    <Icon>
                                      {inputTypeToIconMap[fq.inputType]}
                                    </Icon>
                                    <div className={styles.header}>
                                      <Text
                                        weight="semiBold"
                                        size="sm-md"
                                        noMargin
                                      >
                                        {fq.title}
                                      </Text>
                                      {isShown &&
                                        chartCompatibleTypes.has(
                                          fq.inputType,
                                        ) && (
                                          <div className={styles.chartIcons}>
                                            {chartGraphTypes.map((t) => {
                                              const qInList = questions.find(
                                                (sq) => sq.id === fq.id,
                                              );
                                              const isSelected =
                                                (qInList?.graphType ||
                                                  options?.graphType ||
                                                  'BAR_HORIZONTAL') === t.key;
                                              return (
                                                <Icon
                                                  key={t.key}
                                                  hover
                                                  noMargin
                                                  style={{
                                                    fontSize: '1rem',
                                                    color: isSelected
                                                      ? customColors.BLUE
                                                      : customColors.SECONDARY_LIGHT,
                                                    ...t.iconProps,
                                                  }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setMenuState((prev) => ({
                                                      ...prev,
                                                      questions:
                                                        prev.questions.map(
                                                          (sq) =>
                                                            sq.id === fq.id
                                                              ? {
                                                                  ...sq,
                                                                  graphType:
                                                                    t.key,
                                                                }
                                                              : sq,
                                                        ),
                                                    }));
                                                  }}
                                                >
                                                  {t.icon}
                                                </Icon>
                                              );
                                            })}
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                );
                              })}
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <>
            {loading ? (
              <Spinner className={styles.spinner} />
            ) : (
              options.questions?.map((q) => {
                const qKey = String(q.id);
                const entry = statsByQuestionId.get(qKey);
                const stats = entry?.stats ?? null;
                const hasAnswers = (stats?.total ?? 0) > 0;

                return (
                  <div className={styles.section} key={q.id}>
                    <div className={styles.header}>
                      <div className={styles.flexRow}>
                        <Icon>{inputTypeToIconMap[q.inputType]}</Icon>
                        <Text weight="semiBold" noMargin>
                          {q.title}
                        </Text>
                      </div>
                      {stats ? (
                        <div>
                          <TextWithIcon
                            iconProps={{ fontSize: '1rem' }}
                            icon="rate_review"
                            size="sm"
                            noMargin
                            textAlign="right"
                          >
                            {stats.total}
                          </TextWithIcon>
                        </div>
                      ) : null}
                    </div>
                    {!!hasAnswers && !!stats ? (
                      <div className={styles.stats}>
                        <QuestionStats
                          stats={stats}
                          qid={q.id}
                          graphType={q.graphType || options?.graphType}
                          company={company}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
}

function QuestionStats({ stats, qid, graphType = 'BAR_HORIZONTAL', company }) {
  const getRiskLevel = (value) => {
    const levels = company?.riskMatrix?.levels;
    if (levels) {
      const level = Object.entries(levels).find(
        ([, { min, max }]) =>
          typeof min === 'number' && value >= min && value <= max,
      )?.[0];
      if (level)
        return { level, color: colorLookup[level] ?? colorLookup['Low'] };
    }
    const r = calculateRisk(value);
    return { level: r.level, color: r.color };
  };

  const RiskTag = ({ value }) => {
    if (value == null || !Number.isFinite(Number(value))) return null;
    const { level, color } = getRiskLevel(Number(value));
    if (!level) return null;
    return (
      <span
        style={{
          backgroundColor: color,
          color: '#fff',
          borderRadius: 4,
          padding: '2px 8px',
          fontSize: '0.7rem',
          fontWeight: 600,
          marginLeft: 6,
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
        }}
      >
        {level}
      </span>
    );
  };
  if (!stats || stats.total === 0) return <></>;

  const DateLine = () => {
    const isMulti = stats.kind === 'multi';

    // dayMs → Map<label, count>
    const byDate = useMemo(() => {
      const examples = stats.examples || [];
      const map = new Map();
      for (const ex of examples) {
        if (!ex.dateCreated) continue;
        const dayMs = getLocalTime(ex.dateCreated).startOf('day').valueOf();
        if (!map.has(dayMs)) map.set(dayMs, new Map());
        const inner = map.get(dayMs);
        let key = ex.label;
        if (!key && stats.kind === 'matrix' && ex.impact != null) {
          const risk = ex.risk ?? Number(ex.impact) * Number(ex.probability);
          const { level } = getRiskLevel(Number(risk));
          key = `${risk} - ${level} (Impact ${ex.impact}, Prob ${ex.probability})`;
        }
        key = key || 'Unknown';
        inner.set(key, (inner.get(key) || 0) + 1);
      }
      return map;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const chartData = useMemo(() => {
      const days = Array.from(byDate.keys()).sort((a, b) => a - b);
      if (!days.length) return [];
      if (isMulti) {
        const allLabels = new Set();
        for (const inner of byDate.values())
          for (const label of inner.keys()) allLabels.add(label);
        return Array.from(allLabels).map((label, idx) => ({
          id: label,
          color: bootstrapColors[idx % bootstrapColors.length],
          data: days.map((dayMs) => ({
            x: new Date(dayMs),
            y: byDate.get(dayMs)?.get(label) || 0,
          })),
        }));
      }
      return [
        {
          id: 'Responses',
          color: customColors.BLUE,
          data: days.map((dayMs) => ({
            x: new Date(dayMs),
            y: Array.from(byDate.get(dayMs)?.values() || []).reduce(
              (a, b) => a + b,
              0,
            ),
          })),
        },
      ];
    }, [byDate, isMulti]);

    if (!chartData.length || !chartData[0]?.data?.length) return null;

    return (
      <div style={{ minHeight: '15rem', width: '100%', overflow: 'visible' }}>
        <div style={{ height: '15rem' }}>
          <ResponsiveLine
            data={chartData}
            margin={{ top: 10, right: 40, bottom: 60, left: 40 }}
            enableArea={false}
            animate={false}
            isInteractive
            colors={isMulti ? { datum: 'color' } : [customColors.BLUE]}
            pointSize={6}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            useMesh={false}
            enableGridY
            xScale={{ type: 'time', precision: 'day', useUTC: true }}
            xFormat="time:%Y-%m-%d"
            axisBottom={{
              format: '%Y-%m-%d',
              tickRotation: -30,
              legendOffset: 45,
              legendPosition: 'middle',
            }}
            yScale={{ type: 'linear', min: 0, max: 'auto' }}
            lineWidth={1}
            curve="monotoneX"
            enablePoints
            axisLeft={{
              orient: 'left',
              legendOffset: -40,
              legendPosition: 'middle',
            }}
            theme={{
              axis: {
                ticks: { line: { strokeWidth: 3 }, text: { fontSize: 11 } },
              },
            }}
            enableSlices="x"
            sliceTooltip={({ slice }) => {
              const point = slice.points[0];
              if (!point) return null;
              const dayMs =
                point.data.x instanceof Date
                  ? point.data.x.getTime()
                  : Number(point.data.x);
              const breakdown = byDate.get(dayMs);
              const entries = breakdown
                ? Array.from(breakdown.entries()).sort(([, a], [, b]) => b - a)
                : [];
              return (
                <div className={styles.tooltip}>
                  <Text noMargin weight="semiBold" size="sm">
                    {point.data.xFormatted}
                  </Text>
                  {entries.map(([val, count]) => (
                    <Text size="sm" noMargin key={val}>
                      {val}: <strong>{count}</strong>
                    </Text>
                  ))}
                </div>
              );
            }}
          />
        </div>
        {isMulti && (
          <div className={styles.lineLegend}>
            {chartData.map((s) => (
              <div key={s.id} className={styles.legendText}>
                <span
                  className={styles.color}
                  style={{ background: s.color }}
                />
                <Text size="sm" noMargin>
                  {s.id}
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const Line = ({ examples, height = 180 }) => {
    const byDate = useMemo(() => {
      const map = new Map();
      for (const ex of examples || []) {
        const n = Number(ex?.value);
        if (!Number.isFinite(n) || !ex?.dateCreated) continue;
        const dayMs = getLocalTime(ex.dateCreated).startOf('day').valueOf();
        if (!map.has(dayMs)) map.set(dayMs, []);
        map.get(dayMs).push(n);
      }
      return map;
    }, [examples]);

    const points = useMemo(
      () =>
        Array.from(byDate.entries())
          .sort(([a], [b]) => a - b)
          .map(([dayMs, values]) => ({ x: new Date(dayMs), y: values.length })),
      [byDate],
    );

    if (!points.length) return null;
    const data = [{ id: 'Values', data: points }];

    return (
      <div style={{ height }}>
        <ResponsiveLine
          data={data}
          margin={{ top: 10, right: 18, bottom: 40, left: 44 }}
          enableArea={false}
          animate={false}
          isInteractive
          colors={[customColors.BLUE]}
          pointSize={4}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          useMesh
          enableGridX
          enableGridY
          xScale={{ type: 'time', precision: 'day', useUTC: true }}
          xFormat="time:%Y-%m-%d"
          axisBottom={{
            format: '%Y-%m-%d',
            tickRotation: -30,
            legendOffset: 45,
            legendPosition: 'middle',
          }}
          yScale={{ type: 'linear', min: 0, max: 'auto' }}
          theme={{
            axis: { ticks: { text: { fontSize: 10 } } },
            tooltip: { container: { fontSize: 12 } },
          }}
          tooltip={({ point }) => {
            const dayMs =
              point.data.x instanceof Date
                ? point.data.x.getTime()
                : Number(point.data.x);
            const values = byDate.get(dayMs) || [];
            return (
              <div className={styles.tooltip}>
                <Text noMargin weight="semiBold" size="sm">
                  {point.data.xFormatted}: ({point.data.y})
                </Text>
                {values.map((v, i) => (
                  <Text size="sm" noMargin key={i}>
                    {`- ${v}`}
                  </Text>
                ))}
                <Text weight="semiBold" noMargin size="sm">
                  {`Total: ${values.reduce((a, b) => a + b, 0)}`}
                </Text>
              </div>
            );
          }}
        />
      </div>
    );
  };

  const ExampleList = ({ items, renderItem, step = 3, listKey }) => {
    const total = Array.isArray(items) ? items.length : 0;
    const initial = Math.min(step, total);
    const [visible, setVisible] = useState(initial);

    useEffect(() => {
      setVisible(Math.min(step, total));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qid, total, step, listKey]);
    if (!total) return null;

    const hasMore = visible < total;
    const canShowLess = total > step && visible > step;

    return (
      <div>
        {items.slice(0, visible).map((item, idx) => renderItem(item, idx))}
        <div className={styles.actionsRow}>
          {hasMore ? (
            <div className={styles.actions}>
              <Text
                size="sm"
                color="accentPrimary"
                hover
                noMargin
                onClick={() => setVisible(total)}
                textAlign="right"
              >
                View all ({total - visible})
              </Text>
            </div>
          ) : canShowLess ? (
            <Text
              size="sm"
              color="accentPrimary"
              hover
              noMargin
              onClick={() => setVisible(step)}
              textAlign="right"
            >
              Show less
            </Text>
          ) : (
            <div style={{ height: '0.75rem' }} />
          )}
        </div>
      </div>
    );
  };

  const toChartData = (rows) =>
    (rows || []).map((r, idx) => ({
      label:
        r.label === 'NOT_APPLICABLE'
          ? 'N/A'
          : multiLineTruncate(String(r.label ?? r.key), 11),
      value: typeof r.count === 'number' ? r.count : Number(r.value) || 0,
      color: bootstrapColors[idx % bootstrapColors.length],
      fullLabel: String(r.label ?? r.key),
    }));

  const toPieData = (rows) =>
    (rows || []).map((r, idx) => ({
      id: String(r.label ?? r.key),
      label:
        r.label === 'NOT_APPLICABLE'
          ? 'N/A'
          : multiLineTruncate(String(r.label ?? r.key), 20),
      value: typeof r.count === 'number' ? r.count : Number(r.value) || 0,
      color: bootstrapColors[idx % bootstrapColors.length],
    }));

  const Pie = ({ rows }) => {
    const pieData = toPieData(rows);
    if (!pieData.length) return null;
    return (
      <div className={styles.chartContainer}>
        <NivoPie
          width={500}
          height={300}
          data={pieData}
          isInteractive={false}
          id="id"
          colors={{ datum: 'data.color' }}
          margin={{ left: 0, right: 30, top: 60, bottom: 60 }}
          innerRadius={0.5}
          padAngle={0.8}
          cornerRadius={3}
          borderWidth={1}
          borderColor={{ from: 'color', modifiers: [['darker', 0.5]] }}
          arcLinkLabelsTextColor="#6e6e73"
          sliceLabelsSkipAngle={10}
          sliceLabelsTextColor="#ffffff"
          animate={false}
          motionConfig="stiff"
          theme={{ labels: { text: { fill: 'white', fontSize: 11 } } }}
          arcLinkLabel={(d) => d.data.label}
          sliceLabel={(d) => d.data.label}
        />
        <div className={styles.pieLegend}>
          {pieData.map((s) => (
            <div key={s.id} className={styles.legendText}>
              <span className={styles.color} style={{ background: s.color }} />
              <Text size="sm" noMargin>
                {s.label === 'NOT_APPLICABLE' ? 'N/A' : s.label} -
              </Text>
              <Text size="sm" noMargin weight="semiBold">
                {s.value}
              </Text>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const Bar = ({ rows, height, layout = 'horizontal' }) => {
    const chartData = toChartData(rows);
    const isHorizontal = layout === 'horizontal';
    const h =
      height ??
      (isHorizontal
        ? chartData.length > 1
          ? 40 + chartData.length * 25
          : 70
        : 180);

    return (
      <div style={{ height: h }}>
        <ResponsiveBar
          data={chartData}
          keys={['value']}
          indexBy="label"
          layout={layout}
          margin={
            isHorizontal
              ? { top: 10, right: 50, bottom: 40, left: 60 }
              : { top: 10, right: 50, bottom: 50, left: 40 }
          }
          padding={0.2}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={(bar) => bar.data.color || '#1f77b4'}
          borderColor={{ from: 'color', modifiers: [['darker', 0.6]] }}
          labelSkipWidth={14}
          labelSkipHeight={14}
          labelTextColor={{ from: 'color', modifiers: [['brighter', 10]] }}
          enableGridY
          axisLeft={{ tickRotation: !isHorizontal ? 0 : -45 }}
          axisBottom={
            isHorizontal
              ? undefined
              : {
                  tickRotation: -30,
                  legendOffset: 30,
                  legendPosition: 'middle',
                }
          }
          theme={{
            axis: {
              ticks: { line: { strokeWidth: 3 }, text: { fontSize: 11 } },
            },
          }}
          animate={false}
          tooltip={({ data, value }) => (
            <div className={styles.tooltip}>
              <Text noMargin size="sm">
                <strong>{data.fullLabel || data.label}:</strong> {value}
              </Text>
            </div>
          )}
        />
      </div>
    );
  };
  switch (stats.kind) {
    case 'single':
    case 'multi':
    case 'signature':
      if (graphType === 'PIE') return <Pie rows={stats.rows} />;
      if (graphType === 'BAR')
        return <Bar rows={stats.rows} layout="vertical" />;
      if (graphType === 'LINE') return <DateLine />;
      return <Bar rows={stats.rows} />;

    case 'table': {
      const headers = stats.headers || [];
      const colCount = headers.length || 1;
      const entries = Array.isArray(stats.entries) ? stats.entries : [];

      return (
        <div className={styles.tableWrapper}>
          <div className={styles.headerGrid} style={{ '--cols': colCount }}>
            {headers.map((h, idx) => (
              <div className={styles.headerCell} key={`h-${idx}`}>
                <Text size="sm" weight="semiBold" noMargin>
                  {h}
                </Text>
              </div>
            ))}
          </div>

          <div className={styles.body}>
            {entries.length ? (
              <ExampleList
                items={entries}
                listKey={`table-${colCount}-${entries.length}`}
                step={3}
                renderItem={(entry, eIdx) => (
                  <div key={`e-${eIdx}`} className={styles.entryStart}>
                    {entry.rows?.map((row, rIdx) => (
                      <div
                        className={styles.rowGrid}
                        style={{ '--cols': colCount }}
                        key={`e-${eIdx}-r-${rIdx}`}
                      >
                        {row.map((cell, cIdx) => (
                          <div
                            className={styles.cell}
                            key={`e-${eIdx}-r-${rIdx}-c-${cIdx}`}
                          >
                            <Text size="sm" noMargin>
                              {cell || '-'}
                            </Text>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              />
            ) : (
              <Text size="sm" noMargin>
                <li>No rows</li>
              </Text>
            )}
          </div>
        </div>
      );
    }

    case 'matrix': {
      const riskLevelRanges = Object.entries(
        company?.riskMatrix?.levels || defaultRanges,
      ).sort(([, a], [, b]) => (a.min ?? 0) - (b.min ?? 0));

      return (
        <div>
          <div
            style={{
              display: 'flex',
              gap: 4,
              flexWrap: 'wrap',
              marginBottom: 8,
              justifyContent: 'center',
            }}
          >
            {riskLevelRanges.map(([level, { min, max }]) => (
              <span
                key={level}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    backgroundColor: colorLookup[level] ?? colorLookup['Low'],
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                <Text noMargin size="sm-md">
                  {level} ({min}–{max})
                </Text>
              </span>
            ))}
          </div>
          {(stats.minRisk != null ||
            stats.maxRisk != null ||
            stats.avgRisk != null) && (
            <div className={styles.marginLeft}>
              <Text noMargin size="sm">
                <li>
                  <strong>Min Risk </strong> {stats.minRisk ?? '—'}
                  <RiskTag value={stats.minRisk} />
                </li>
              </Text>
              <Text noMargin size="sm">
                <li>
                  <strong>Max Risk </strong> {stats.maxRisk ?? '—'}
                  <RiskTag value={stats.maxRisk} />
                </li>
              </Text>
              <Text noMargin size="sm">
                <li>
                  <strong>Average Risk </strong>
                  {stats.avgRisk != null
                    ? Number.isInteger(stats.avgRisk)
                      ? stats.avgRisk
                      : stats.avgRisk.toFixed(2)
                    : '—'}
                  <RiskTag value={stats.avgRisk} />
                </li>
              </Text>
            </div>
          )}
          {graphType === 'LINE' ? (
            <DateLine />
          ) : (
            !!stats?.riskRows?.length &&
            (() => {
              const riskRows = stats.riskRows.map((r) => {
                const { level } = getRiskLevel(Number(r.key));
                return {
                  ...r,
                  label: level ? `${level} (${r.key})` : r.label,
                };
              });
              return graphType === 'PIE' ? (
                <Pie rows={riskRows} />
              ) : (
                <Bar
                  rows={riskRows}
                  layout={graphType === 'BAR' ? 'vertical' : 'horizontal'}
                />
              );
            })()
          )}
        </div>
      );
    }

    case 'numeric': {
      const statText = (
        <div className={styles.numberText}>
          <div>
            <Text noMargin size="sm">
              <li>
                <strong>Min </strong> {stats.min ?? '—'}
              </li>
            </Text>
          </div>
          <Text noMargin size="sm">
            <li>
              <strong>Max </strong> {stats.max ?? '—'}
            </li>
          </Text>
          <Text noMargin size="sm">
            <li>
              <strong>Average </strong>
              {stats.avg?.toFixed ? stats.avg.toFixed(2) : stats.avg ?? '—'}
            </li>
          </Text>
          <Text noMargin size="sm">
            <li>
              <strong>Total </strong>
              {stats.sum?.toFixed ? stats.sum.toFixed(2) : stats.sum ?? '—'}
            </li>
          </Text>
        </div>
      );
      if (graphType === 'PIE') {
        const belowAvg = (stats.examples || []).filter(
          (e) => Number(e.value) < stats.avg,
        ).length;
        const atOrAbove = (stats.examples || []).length - belowAvg;
        const numericPieRows = [
          { label: 'Below Average', count: belowAvg },
          { label: 'At or Above Average', count: atOrAbove },
        ];
        return (
          <div>
            {statText}
            <div style={{ flex: 1 }}>
              <Pie rows={numericPieRows} height={160} />
            </div>
          </div>
        );
      }
      return (
        <div>
          {statText}
          <Line examples={stats.examples} />
        </div>
      );
    }

    case 'image': {
      const items = stats?.examples || [];
      return (
        <div className={styles.marginLeft}>
          <ExampleList
            items={items}
            listKey={`image-${qid}`}
            renderItem={(ex, idx) =>
              !!ex ? (
                <img
                  className={styles.image}
                  key={idx}
                  src={ex.url}
                  alt="image_response"
                  onClick={() => openFileViewer({ url: ex.url })}
                />
              ) : null
            }
          />
        </div>
      );
    }

    case 'file': {
      const items = stats?.examples || [];
      if (!items.length) {
        return (
          <div className={styles.marginLeft}>
            <Text size="sm" noMargin>
              <li>No files</li>
            </Text>
          </div>
        );
      }
      const getExt = (u) => {
        const s = String(u || '');
        const noQuery = s.split('?')[0].split('#')[0];
        const last = noQuery.split('/').pop() || '';
        const i = last.lastIndexOf('.');
        return i >= 0 ? last.slice(i + 1).toLowerCase() : '';
      };

      return (
        <ExampleList
          items={items}
          listKey={`file-${qid}`}
          renderItem={(e, idx) => {
            const url = e?.url || '';
            const name = e?.name || url || 'Attachment';
            const ext = getExt(url);
            const isImage = !!ext && imageExtensions.includes(ext);
            const src = isImage ? url : findFileIcon(ext);
            return (
              <div key={`${url}-${idx}`} className={styles.flexRow}>
                {src ? (
                  <img className={styles.thumbnail} src={src} alt="file" />
                ) : (
                  <Icon>attachment</Icon>
                )}
                <Text
                  size="sm"
                  weight="semiBold"
                  color="accentPrimary"
                  noMargin
                  hover
                  onClick={() => downloadFile(url, name)}
                >
                  {name}
                </Text>
              </div>
            );
          }}
        />
      );
    }

    case 'text': {
      const items = stats?.examples || [];
      return (
        <div className={styles.marginLeft}>
          <ExampleList
            items={items}
            listKey={`text-${qid}`}
            renderItem={(ex, idx) => (
              <Text size="sm" key={`${idx}`} noMargin>
                <li>{ex.value}</li>
              </Text>
            )}
          />
        </div>
      );
    }

    default:
      return <></>;
  }
}

const statsOptionsEqual = (prev, next) => {
  if (prev.isEditing !== next.isEditing) return false;
  if (prev.attachmentId !== next.attachmentId) return false;
  if (prev.setMenuState !== next.setMenuState) return false;
  if (prev.setStatsPDF !== next.setStatsPDF) return false;
  const o1 = prev.options;
  const o2 = next.options;
  return (
    o1?.template === o2?.template &&
    o1?.questions === o2?.questions &&
    o1?.workspaces === o2?.workspaces &&
    o1?.daysBack === o2?.daysBack &&
    o1?.timeRange === o2?.timeRange &&
    o1?.title === o2?.title &&
    o1?.description === o2?.description &&
    o1?.graphType === o2?.graphType
  );
};

export default React.memo(ReportBuildMenuStats, statsOptionsEqual);
